import React, { useState, useEffect, useRef } from "react";
import {
  TableContainer,
  Paper,
  Typography,
  Box,
  PaginationItem,
  IconButton,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { api, get } from "../../../../utils/api";
import { styled } from "@mui/material/styles";
import DateRangePickerComponent from "../../Roles and Permissions/Filters/Date";
import PaginationBox from "../../../../Components/Masters/Pagination";
import DeleteDialog from "../../../../Components/Masters/DeleteDialog";
import MenuActions from "../../../../Components/Masters/MenuActions";
import FilterDrawer from "../../../../Components/Masters/FilterDrawer";
import TopMenu from "../../../../Components/Masters/TopMenu";
import MessagePopup from "../../../../Components/Masters/MessagePopup";
import TableHeading from "../../../../Components/Masters/TableHeading";
import MenuIcon from "../../../../Components/Masters/MenuIcon";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import MastersSidebar from "../../../../Layout/MastersSidebar";
import Unauthorized from "../../../../Components/Unauthorized/Unauthorized";
import AddCustomerMaster from "./AddCustomerMaster";
import ViewCustomerMaster from "./ViewCustomerMaster";
import { useUser } from "../../../../context/AuthContext";
import { formatDateMonth } from "../../../../utils/formatDate";
import { FacilityColumnHeading } from "../../../../utils/facilityColumn";

const getFacilityName = (item) =>
  item?.facility?.facilityName ||
  item?.facilityName?.facilityName ||
  (typeof item?.facilityName === "string" ? item.facilityName : "") ||
  "";

const shouldShowFacilityColumn = (user) =>
  Boolean(user?.companyAdmin) && !user?.facility;

const customerGridRowSx = (columnWidth, stretch) => ({
  display: "grid",
  gridTemplateColumns: columnWidth,
  ...(stretch && { alignItems: "stretch" }),
  borderBottom: "1px solid #ddd",
});

const customerHeaderCellSx = {
  fontWeight: "bold",
  padding: "8px 12px",
  textAlign: "left",
  borderRight: "1px solid #ddd",
  whiteSpace: "normal",
  wordBreak: "break-word",
  display: "flex",
  alignItems: "center",
  minHeight: "100%",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box",
};

const CustomerColumnHeading = ({ title, handleSort, field, sortConfig }) => (
  <Box
    sx={{
      ...customerHeaderCellSx,
      paddingLeft: title === "Date" ? "16px" : "12px",
      gap: "6px",
    }}
  >
    <Box
      component="span"
      sx={{
        flex: 1,
        minWidth: 0,
        lineHeight: 1.3,
      }}
    >
      {title}
    </Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexShrink: 0,
        width: "22px",
      }}
    >
      <IconButton
        size="small"
        sx={{ p: 0, height: "14px", width: "22px" }}
        onClick={() => handleSort(field, "asc")}
      >
        <ExpandLess
          sx={{
            fontSize: "18px",
            color:
              sortConfig.key === field && sortConfig.direction === "asc"
                ? "blue"
                : "black",
          }}
        />
      </IconButton>
      <IconButton
        size="small"
        sx={{ p: 0, height: "14px", width: "22px" }}
        onClick={() => handleSort(field, "desc")}
      >
        <ExpandMore
          sx={{
            fontSize: "18px",
            color:
              sortConfig.key === field && sortConfig.direction === "desc"
                ? "blue"
                : "black",
          }}
        />
      </IconButton>
    </Box>
  </Box>
);

const customerDataCellSx = {
  padding: "8px 12px",
  textAlign: "left",
  borderRight: "1px solid #ddd",
  whiteSpace: "normal",
  wordBreak: "break-word",
  fontSize: "14px",
  color: "black",
  lineHeight: 1.4,
  display: "flex",
  alignItems: "center",
  minHeight: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const customerActionHeaderSx = {
  fontWeight: "bold",
  padding: "8px 12px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100%",
  boxSizing: "border-box",
};

const customerActionCellSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100%",
  boxSizing: "border-box",
};

const StyledPaginationItem = styled(PaginationItem)(() => ({
  "&.Mui-selected": {
    backgroundColor: "green",
    color: "#fff",
    fontWeight: "bold",
  },
  "&:hover": {
    backgroundColor: "#ededed",
  },
}));

const formInputData = [
  {
    fieldName: "productName",
    title: "Product Name",
  },
  {
    fieldName: "customerVendorName",
    title: "Customer/Vendor Name",
    placeholder: "Enter Customer/Vendor Name",
  },

  {
    fieldName: "picName",
    title: "PIC Name",
    placeholder: "Enter PIC Name",
  },

  {
    fieldName: "email",
    title: "Email",
    placeholder: "Enter Email",
  },
  {
    fieldName: "country",
    title: "Location of Processing (Country)",
  },
  {
    fieldName: "processingMethodsUsed",
    title: "Processing methods used",
    placeholder: "Enter method",
  },
];

const viewInputData = [
  { fieldName: "productName", title: "Product Name" },
  { fieldName: "customerVendorName", title: "Customer/Vendor Name" },
  { fieldName: "picName", title: "PIC Name" },
  { fieldName: "country", title: "Location of Processing (Country)" },
  { fieldName: "email", title: "Email" },
  { fieldName: "processingMethodsUsed", title: "Processing methods used" },
];

const CustomerMaster = () => {
  const { user } = useUser();
  const showFacilityColumn = shouldShowFacilityColumn(user);
  const columnWidth = showFacilityColumn
    ? "11% 15% 16% 13% 11% 19% 8%"
    : "12% 17% 17% 20% 25% 8%";
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [addData, setAddData] = useState(false);
  const [viewData, setViewData] = useState(false);
  const [element, setElement] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("Date");
  const [drawerFilterOpen, setDrawerFilterOpen] = useState(false);
  const [tempFilter, setTempFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [rowsPerPage] = useState(10);
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const filters = ["Date"];
  const appliedFilters = { Date: !!(startDate || endDate) };
  const selectedFields = {};
  const setSelectedFields = () => { };
  const errors = { Date: false };
  const setErrors = () => { };

  const filtersToApply = {
    startDate: tempFilter?.startDate ? tempFilter?.startDate.format("YYYY-MM-DD") : "",
    endDate: tempFilter?.endDate ? tempFilter?.endDate.format("YYYY-MM-DD") : "",
  };

  const fetchData = async (filtersArg = isFiltersApplied ? filtersToApply : {}) => {
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(rowsPerPage),
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...Object.fromEntries(
          Object.entries(filtersArg).filter(([, v]) => v !== "" && v !== undefined && v !== null),
        ),
      }).toString();

      const response = await get(`/master/customerMasters?${queryParams}`);
      setData(response?.data?.data || []);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching customer master:", error);
      setData([]);
      setTotalPages(1);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, sortConfig]);

  const updatePage = () => {
    if (page > 1 && data.length === 1) {
      setPage(page - 1);
    }
    fetchData();
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setElement(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setAddData(true);
    setViewData(false);
  };

  const handleView = () => {
    handleMenuClose();
    setViewData(true);
    setAddData(false);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialog(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchData(filtersToApply);
    setStartDate(tempFilter?.startDate);
    setEndDate(tempFilter?.endDate);
    setIsFiltersApplied(!!(tempFilter?.startDate || tempFilter?.endDate));
    setDrawerFilterOpen(false);
  };

  const handleResetFilters = async () => {
    setTempFilter({ startDate: null, endDate: null });
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    setIsFiltersApplied(false);
    await fetchData({});
    setDrawerFilterOpen(false);
  };

  const handleFilterDrawer = () => {
    setTempFilter({
      startDate: startDate,
      endDate: endDate,
    });
    setDrawerFilterOpen(false);
  };

  const handleSort = (column, direction) => {
    setSortConfig({ key: column, direction });
  };

  const columnHeadings = showFacilityColumn
    ? [
        { fieldName: "createdAt", title: "Date" },
        { fieldName: "facilityName", title: "Facility Name", isFacility: true },
        { fieldName: "customerVendorName", title: "Customer/Vendor Name" },
        { fieldName: "picName", title: "PIC Name" },
        { fieldName: "country", title: "Country" },
        { fieldName: "email", title: "Email" },
      ]
    : [
        { fieldName: "createdAt", title: "Date" },
        { fieldName: "customerVendorName", title: "Customer/Vendor Name" },
        { fieldName: "picName", title: "PIC Name" },
        { fieldName: "country", title: "Country" },
        { fieldName: "email", title: "Email" },
      ];

  const renderCellValue = (item, el) => {
    if (el.fieldName === "createdAt") {
      return formatDateMonth(item.createdAt);
    }
    if (el.isFacility) {
      return getFacilityName(item) || "N/A";
    }
    const value = getFieldData(item, el.fieldName);
    if (typeof value === "object" && value !== null && value.name) {
      return value.name;
    }
    return value === 0 ? 0 : value || "N/A";
  };

  const renderSelectedFilter = () => {
    return (
      <DateRangePickerComponent
        startDate={tempFilter?.startDate}
        endDate={tempFilter?.endDate}
        setStartDate={(date) => setTempFilter((prev) => ({ ...prev, startDate: date }))}
        setEndDate={(date) => setTempFilter((prev) => ({ ...prev, endDate: date }))}
        setErrors={setErrors}
        errors={errors.Date}
      />
    );
  };

  const tableContainerRef = useRef(null);
  const [isShortContent, setIsShortContent] = useState(false);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const tableHeight = container.scrollHeight;
      const screenHeight = window.innerHeight;
      setIsShortContent(tableHeight < screenHeight / 1.5);
    }
  }, [data]);

  const getFieldData = (item, fieldName) => {
    if (fieldName === "productName") {
      const p = item?.productName;
      if (Array.isArray(p) && p.length > 0) {
        return p[0]?.productName || p[0]?.name || "-";
      }
      return p?.productName || p?.name || p || "-";
    }
    if (fieldName === "processingMethodsUsed") {
      const value = item?.processingMethodsUsed;
      return Array.isArray(value) ? value.join(", ") : value || "-";
    }
    return item?.[fieldName];
  };

  const tableGridRowSx = customerGridRowSx(columnWidth, showFacilityColumn);

  const tableHeaderRowSx = {
    ...customerGridRowSx(columnWidth, showFacilityColumn),
    borderTop: "1px solid #ddd",
  };

  return (
    <MainDashboardLayout sidebar={<MastersSidebar />}>
      <Unauthorized requiredModule="Setup | Master">
        <Box sx={{ backgroundColor: "#ededed", width: "100%" }}>
          <MessagePopup {...{ snackOpen, setsnackOpen }} />
          <AddCustomerMaster
            id={element ? element._id : ""}
            addData={addData}
            setAddData={setAddData}
            element={element}
            addUrl="/master/customerMasters"
            updateUrl="/master/customerMasters"
            fetchData={fetchData}
            setsnackOpen={setsnackOpen}
            formInputData={formInputData}
          />
          <ViewCustomerMaster
            data={element}
            viewData={viewData}
            setViewData={setViewData}
            inputData={viewInputData}
            handleEdit={handleEdit}
          />
          {deleteDialog && (
            <DeleteDialog
              fetchData={updatePage}
              {...{
                deleteDialog,
                setDeleteDialog,
                id: element?._id,
                url: `${api}/master/customerMasters`,
                method: "delete",
                setsnackOpen,
              }}
            />
          )}
          {drawerFilterOpen && (
            <FilterDrawer
              {...{
                renderSelectedFilter,
                drawerFilterOpen,
                setDrawerFilterOpen,
                handleApplyFilters,
                handleFilterDrawer,
                setSelectedFilter,
                handleResetFilters,
                selectedFilter,
                filters,
                appliedFilters,
                selectedFields,
                setSelectedFields,
              }}
            />
          )}
          <TopMenu
            setData={setElement}
            setAddData={setAddData}
            {...{
              heading: "Scope 3",
              setDrawerFilterOpen,
              filterApplied: isFiltersApplied,
            }}
          />
          <TableContainer
            component={Paper}
            ref={tableContainerRef}
            sx={{ maxWidth: "100%", overflowX: "auto" }}
          >
            <Box sx={{ minWidth: 900 }}>
              <TableHeading heading={"Customer Master"} />
              <Box sx={tableHeaderRowSx}>
                {columnHeadings.map((el) =>
                  el.isFacility ? (
                    <FacilityColumnHeading
                      key={el.fieldName}
                      handleSort={handleSort}
                      sortConfig={sortConfig}
                    />
                  ) : (
                    <CustomerColumnHeading
                      key={el.fieldName}
                      title={el.title}
                      handleSort={handleSort}
                      field={el.fieldName}
                      sortConfig={sortConfig}
                    />
                  )
                )}
                <Typography sx={customerActionHeaderSx}>Action</Typography>
              </Box>
              {data.length === 0 ? (
                <NoDataFound />
              ) : (
                data.map((item) => (
                  <Box key={item._id} sx={tableGridRowSx}>
                    {columnHeadings.map((el) => (
                      <Typography
                        key={el.fieldName}
                        sx={{
                          ...customerDataCellSx,
                          ...(el.fieldName === "createdAt" && { paddingLeft: "16px" }),
                        }}
                      >
                        {renderCellValue(item, el)}
                      </Typography>
                    ))}
                    <Box sx={customerActionCellSx}>
                      <MenuIcon {...{ handleMenuClick, field: item }} />
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </TableContainer>
          {totalPages > 1 && (
            <PaginationBox
              isShortContent={isShortContent}
              {...{ totalPages, page, handleChangePage, StyledPaginationItem }}
            />
          )}
          <MenuActions
            {...{
              id: element?._id,
              handleView,
              handleEdit,
              handleDelete,
              anchorEl,
              handleMenuClose,
            }}
          />
        </Box>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default CustomerMaster;
